import VirtualList from 'rc-virtual-list';
import * as React from 'react';
import { BaSeI18nContext } from '../../contexts/i18n';
import { useNextHashId } from '../../hooks/next-id';
import { useOutsideEvent } from '../../hooks/outside-event';
import { BaSeTheme } from '../../theme';
import { getFormattedDataset } from '../../utils/dataset-utils';
import { idGenerator } from '../../utils/id-generator';
import { joinWithSeparators, normalize } from '../../utils/string-utils';
import { BaSeIconButton } from '../button/shape-button/icon-button';
import { BaSeHelperText } from '../helpers/helper-text/helper-text';
import { BaSePopupButton } from '../helpers/popup-button/popup-button';
import { BaSeIcon } from '../image/icon';
import { BaSeCheckbox } from '../input/checkbox/checkbox';
import { BaSeInput } from '../input/text/input';
import { BaSeLoadingSpinner } from '../loading/spinner';
import { BaSeParagraph } from '../typography/paragraph/paragraph';
import { BaSeSmall1 } from '../typography/small/small1';
import { mapBaSeSelectColor, mapSelectValues } from './map-select-style';
import { SelectProps, SelectValue, ValueId } from './multiple-select';
import {
  OptionPickerBoxWrapper,
  OptionPickerComplementWrapper,
  OptionPickerExtraActionButton,
  OptionPickerExtraActionsHeaderWrapper,
  OptionPickerFilterContainer,
  OptionPickerHeaderLabelContainer,
  OptionPickerInfoWrapper,
  OptionPickerListUl,
  OptionPickerOuterList,
  WrapperVirtualList,
} from './option-picker-style';
import {
  ButtonInfoContainerHelper,
  DetailButtonWrapper,
  HideTopOfWrapperOptions,
  MoreInfoContainer,
  OptionDetail,
  OptionDetailButton,
  OptionDetailHeader,
  SelectInput,
  StyledLabel,
  StyledSubLabel,
  WrapperHelper,
  WrapperIcon,
  WrapperInputLabel,
  WrapperMultipleInformation,
  WrapperOption,
  WrapperSelect,
} from './select-styled';

const idSequence = idGenerator();
const rowHeight = 34;

function extractPixels(value: number | string | null): number {
  return typeof value === 'number'
    ? value
    : typeof value === 'string' && value.includes('px')
      ? +value.replace(/\D/g, '')
      : 0;
}

interface OptionPickerExtraActions {
  label: string;
  action: () => void;
  icon?: string;
}

export interface OptionPickerItem extends SelectValue {
  icon?: string;
  complement?: string;
  detail?: {
    title: string;
    description: string;
  };
}

export interface OptionPickerProps<
  OptionValue extends OptionPickerItem = OptionPickerItem,
> extends Omit<
    SelectProps,
    'values' | 'searchable' | 'removeEmptyValue' | 'onChange'
  > {
  values: OptionValue[];
  headerLabel?: string | Map<string, ValueId[]>;
  extraActions?: OptionPickerExtraActions[];
  enableSelectAll?: boolean;
  maxMenuHeight?: string;
  maxOptionsSelected?: number;
  showItemDetailButton?: boolean;
  searchPlaceholder?: string;
  optionNotFoundLabel?: string;
  menuAlwaysIsOpen?: boolean;
  withFilterText?: boolean;
  withVirtualList?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  onBlur?: () => void;
  onChange: (selectedValue: OptionValue[]) => void;
}

type Option = OptionPickerItem & {
  ref?: React.RefObject<HTMLLIElement>;
};

export function BaSeOptionPicker<OptionValue extends OptionPickerItem>({
  id: externalId,
  name,
  dataset,
  isDisabled,
  width = undefined,
  size = 'medium',
  label,
  helpButtonProps,
  showHelpButton,
  subLabel,
  color,
  overrideColor = {},
  initialSelectedValuesId = [0],
  hasError = false,
  emptyValueLabel = '',
  complement = '',
  moreInfoLabel = '',
  moreInfoDetails = '',
  values = [],
  headerLabel = '',
  extraActions = [],
  enableSelectAll = true,
  maxMenuHeight = '200px',
  maxOptionsSelected,
  showItemDetailButton = false,
  searchPlaceholder = '',
  optionNotFoundLabel = '',
  menuAlwaysIsOpen = false,
  withFilterText = true,
  withVirtualList = false,
  isLoading = false,
  loadingText,
  onBlur,
  onChange = () => {},
}: OptionPickerProps<OptionValue>) {
  const { getMessage } = React.useContext(BaSeI18nContext);

  const [isOpen, setIsOpen] = React.useState<boolean>(menuAlwaysIsOpen);
  const [colorType, setColorType] = React.useState('default');
  const [currentExtraFocus, setCurrentExtraFocus] = React.useState<number[]>(
    [],
  );
  const [inputFilter, setInputFilter] = React.useState<string>('');
  const [selectedValuesId, setSelectedValuesId] = React.useState<ValueId[]>(
    initialSelectedValuesId,
  );
  const [refValues, setRefValues] = React.useState<Option[]>([]);

  const isReadonly = React.useMemo(
    () =>
      values.every(
        (item) => selectedValuesId[0]?.toString() === item.id?.toString(),
      ),
    [values, selectedValuesId],
  );

  const handleIsOpen = React.useCallback(
    (openOrClose: boolean) => {
      if (isReadonly || isLoading) {
        return setIsOpen(false);
      }
      if (!menuAlwaysIsOpen) {
        setIsOpen(openOrClose);
      }
    },
    [menuAlwaysIsOpen, isReadonly, isLoading],
  );

  const inputFilterRef = React.useRef<HTMLInputElement>(null);
  const selectInputRef = React.useRef<HTMLInputElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  useOutsideEvent<HTMLDivElement>(wrapperRef, handleIsOpen);

  const generatedId = useNextHashId(idSequence);
  const id = externalId ?? generatedId;

  const formattedDataset = getFormattedDataset(dataset);

  const getColorsAtributes = React.useCallback(() => {
    if (hasError) return mapSelectValues('destructive');
    if (color) return mapSelectValues(color);
    return mapSelectValues('default');
  }, [color, hasError]);

  const isHeaderLabelMap = React.useCallback(
    (
      headerLabel: string | Map<string, ValueId[]> | undefined,
    ): headerLabel is Map<string, ValueId[]> => {
      return headerLabel instanceof Map;
    },
    [],
  );

  const updateValue = React.useCallback(
    (selected: OptionValue) => {
      if (maxOptionsSelected === 1) {
        setSelectedValuesId([selected.id]);
        handleIsOpen(false);
        return;
      }

      if (selectedValuesId.indexOf(selected.id) > -1) {
        return setSelectedValuesId((input) => {
          return input.filter(
            (val) => val?.toString() !== selected.id?.toString(),
          );
        });
      }
      if (
        !maxOptionsSelected ||
        (selectedValuesId.length < maxOptionsSelected && maxOptionsSelected > 0)
      ) {
        setSelectedValuesId((input) => input?.concat([selected.id]));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedValuesId, maxOptionsSelected],
  );

  const onFocusInput = React.useCallback(
    () => handleIsOpen(!isDisabled && true),
    [handleIsOpen, isDisabled],
  );

  const {
    style: { bcCHover, bgC, boxShadowFocus, border },
  } = React.useMemo(() => getColorsAtributes(), [getColorsAtributes]);

  const handleFilter = React.useCallback(
    (item: OptionPickerItem) => {
      return (
        !inputFilter ||
        normalize(
          item.label +
            (item?.complement ?? '') +
            (item?.detail?.description ?? '') +
            (item?.detail?.title ?? ''),
        ).includes(normalize(inputFilter))
      );
    },
    [inputFilter],
  );

  const filteredValues = React.useMemo(
    () => refValues.filter(handleFilter),
    [handleFilter, refValues],
  );

  const selectAll = React.useCallback(() => {
    if (filteredValues.length > selectedValuesId.length) {
      setSelectedValuesId(filteredValues.map((option) => option.id));
    } else {
      setSelectedValuesId([]);
    }
  }, [filteredValues, selectedValuesId.length]);

  const emitChange = React.useCallback(() => {
    const emitValues = values.filter(
      (val) => selectedValuesId.indexOf(val.id) > -1,
    );
    onChange(emitValues);
  }, [onChange, selectedValuesId, values]);

  const addIndexExtra = React.useCallback(
    (index: number) => {
      if (currentExtraFocus.findIndex((i) => i === index) === -1) {
        setCurrentExtraFocus((prev) => [...prev, index]);
      }
    },
    [currentExtraFocus],
  );

  const removeIndexExtra = React.useCallback(
    (index: number) => {
      const auxCurrentExtraFocus = [...currentExtraFocus];
      const ind = auxCurrentExtraFocus.findIndex((i) => i === index);
      if (ind > -1) {
        auxCurrentExtraFocus.splice(ind, 1);
      }
      setCurrentExtraFocus([...auxCurrentExtraFocus]);
    },
    [currentExtraFocus],
  );

  const indexInExtra = React.useCallback(
    (index: number) => {
      return currentExtraFocus.findIndex((i) => i === index) !== -1;
    },
    [currentExtraFocus],
  );

  const extraActionCall = React.useCallback((action: () => void) => {
    action();
    handleIsOpen(false);
  }, []);

  const checkboxIsDisabled = React.useCallback(
    (opt: OptionPickerItem) => {
      return (
        !!maxOptionsSelected &&
        selectedValuesId.length === maxOptionsSelected &&
        selectedValuesId.indexOf(opt.id) === -1
      );
    },
    [maxOptionsSelected, selectedValuesId],
  );

  const removeDetailClassFromList = React.useCallback(() => {
    refValues.forEach((refVal) => {
      refVal.ref?.current?.classList.remove('detail');
    });
  }, [refValues]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLLIElement>, option?: Option) => {
      const spaceBarKey = event.key === ' ';
      const enterKey = event.key === 'Enter';

      if (enterKey || spaceBarKey) {
        return option ? updateValue(option as OptionValue) : selectAll();
      }
    },
    [updateValue, selectAll],
  );

  const colorCheckBox = React.useMemo(
    () =>
      colorType === 'default' && !hasError && color
        ? color
        : mapBaSeSelectColor?.[colorType]?.checkbox,
    [color, colorType, hasError],
  );

  const colorInput = React.useMemo(
    () =>
      colorType === 'default' && !hasError && color
        ? color
        : mapBaSeSelectColor?.[colorType]?.color,
    [color, colorType, hasError],
  );

  const renderOption = React.useCallback(
    (option: Option) => (
      <WrapperOption
        key={option.id?.toString()}
        aria-selected={selectedValuesId.indexOf(option.id) > -1}
        ref={option?.ref}
        multiple={true}
        bcCHover={overrideColor.selectHoverColor ?? bcCHover}
        bgC={bgC}
        isActive={false}
        role="option"
        tabIndex={0}
        isOptionPickerItem={true}
        onKeyDown={(event) => handleKeyDown(event, option)}
        onClick={(e) => {
          e.preventDefault();
          updateValue(option as OptionValue);
        }}
        onPointerEnter={
          !showItemDetailButton
            ? () => option.ref?.current?.classList.add('detail')
            : undefined
        }
        onFocus={
          !showItemDetailButton
            ? () => option.ref?.current?.classList.add('detail')
            : undefined
        }
        onPointerLeave={
          !showItemDetailButton
            ? () => option.ref?.current?.classList.remove('detail')
            : undefined
        }
        onBlur={
          !showItemDetailButton
            ? () => option.ref?.current?.classList.remove('detail')
            : undefined
        }
      >
        <OptionPickerInfoWrapper>
          {option.icon && (
            <BaSeIcon
              size="1em"
              color={colorCheckBox}
              name={option.icon}
              description={getMessage('icon.label', option.label)}
            />
          )}
          {(!maxOptionsSelected || maxOptionsSelected > 1) && (
            <BaSeCheckbox
              align="top-left"
              isInteractive={false}
              label={option.label}
              color={colorCheckBox}
              size="medium"
              disabled={checkboxIsDisabled(option)}
              checked={selectedValuesId.indexOf(option.id) > -1}
            />
          )}
          {!(!maxOptionsSelected || maxOptionsSelected > 1) && (
            <BaSeParagraph>{option.label}</BaSeParagraph>
          )}
        </OptionPickerInfoWrapper>
        {option.detail && (
          <>
            {showItemDetailButton && (
              <DetailButtonWrapper>
                <BaSeIcon
                  color={mapBaSeSelectColor[colorType].item}
                  name={'question-circle'}
                  size="1em"
                  description={getMessage('select.showDetail')}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeDetailClassFromList();
                    option.ref?.current?.classList.add('detail');
                  }}
                />
              </DetailButtonWrapper>
            )}
            <OptionDetail
              isOptionPicker={true}
              onClick={(e) => e.stopPropagation()}
            >
              <OptionDetailHeader>
                <BaSeSmall1 color={bgC} isBold={true}>
                  {option.detail.title}
                </BaSeSmall1>
                <OptionDetailButton>
                  <BaSeIconButton
                    color={colorCheckBox}
                    nameIcon="close-big"
                    descriptionIcon={getMessage(
                      'buttonIcon.iconDescription.close',
                    )}
                    size="extra-small"
                    type="tertiary"
                    onClick={() => {
                      option.ref?.current?.classList.remove('detail');
                    }}
                  />
                </OptionDetailButton>
              </OptionDetailHeader>
              <BaSeSmall1
                color={mapBaSeSelectColor[colorType].item}
                isThin={true}
              >
                {option.detail.description}
              </BaSeSmall1>
            </OptionDetail>
          </>
        )}
        {!!option.complement && (
          <OptionPickerComplementWrapper>
            <BaSeParagraph
              isThin={true}
              color={BaSeTheme.colors.institucionais.cinzaSebrae30}
            >
              {option.complement}
            </BaSeParagraph>
          </OptionPickerComplementWrapper>
        )}
      </WrapperOption>
    ),
    [
      bcCHover,
      bgC,
      colorType,
      selectedValuesId,
      maxOptionsSelected,
      showItemDetailButton,
      overrideColor.selectHoverColor,
      checkboxIsDisabled,
      getMessage,
      handleKeyDown,
      removeDetailClassFromList,
      updateValue,
      colorCheckBox,
    ],
  );

  const renderSelectInputText = React.useCallback(
    () =>
      isLoading
        ? loadingText ?? getMessage('loading.description')
        : selectedValuesId.length < 1
          ? emptyValueLabel || getMessage('select.emptyValue')
          : selectedValuesId.length > 3
            ? getMessage('select.selectedCounter', selectedValuesId.length)
            : joinWithSeparators(
                refValues
                  .filter((val) => selectedValuesId.indexOf(val.id) > -1)
                  .map((val) => val.label),
                getMessage('list.separator'),
                getMessage('list.finalSeparator'),
              ),
    [
      emptyValueLabel,
      getMessage,
      isLoading,
      loadingText,
      refValues,
      selectedValuesId,
    ],
  );

  React.useEffect(() => {
    setRefValues(
      values.map((item) => ({
        ...item,
        ref: React.createRef<HTMLLIElement>(),
      })),
    );
  }, [values]);

  React.useEffect(() => {
    setSelectedValuesId(initialSelectedValuesId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedValuesId.toString()]);

  React.useEffect(() => {
    setColorType(isDisabled ? 'disabled' : hasError ? 'error' : 'default');
  }, [hasError, isDisabled]);

  React.useEffect(() => {
    emitChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValuesId.toString()]);

  React.useEffect(() => {
    if (!isOpen) {
      setCurrentExtraFocus([]);
    } else {
      inputFilterRef.current?.focus();
    }
  }, [isOpen, inputFilterRef]);

  return (
    <WrapperSelect
      ref={wrapperRef}
      isDisabled={!!isDisabled}
      width={width}
      onBlur={(e) => {
        if (e.relatedTarget === null) {
          onBlur?.();
        }
      }}
    >
      <WrapperInputLabel>
        <StyledLabel
          fontSize={size === 'small' ? '13px' : '16px'}
          lineHeight={size === 'small' ? '16px' : '23px'}
          htmlFor={id}
        >
          {label}
        </StyledLabel>
        <StyledSubLabel htmlFor={id}>{subLabel}</StyledSubLabel>
        {showHelpButton && (
          <ButtonInfoContainerHelper>
            <BaSePopupButton {...helpButtonProps} />
          </ButtonInfoContainerHelper>
        )}
      </WrapperInputLabel>
      {!menuAlwaysIsOpen && (!isReadonly || isLoading) && (
        <WrapperIcon
          hasLabel={!!label}
          vSize={size}
          isOpen={isOpen}
          searchable={false}
        >
          {isLoading ? (
            <BaSeLoadingSpinner
              diameter={16}
              description={loadingText ?? getMessage('loading.description')}
              color={BaSeTheme.colors.institucionais.cinzaSebrae30}
            />
          ) : (
            <BaSeIcon
              description={
                isOpen
                  ? getMessage('select.closeOptions')
                  : getMessage('select.openOptions')
              }
              name="arrow-head-down"
              size={16}
              onClick={isDisabled ? undefined : () => handleIsOpen(!isOpen)}
              color={colorInput}
            />
          )}
        </WrapperIcon>
      )}
      <SelectInput
        id={id}
        name={name}
        {...formattedDataset}
        ref={selectInputRef}
        hasSelectedOption={false}
        disabled={isDisabled}
        vSize={size}
        boxShadowFocus={boxShadowFocus}
        readOnly={true}
        border={border}
        bgC={mapBaSeSelectColor[colorType].iconColor}
        colorType={colorType}
        value={renderSelectInputText()}
        isOpen={isOpen}
        hasError={hasError}
        onChange={(newValue) => updateValue(newValue as unknown as OptionValue)}
        onFocus={onFocusInput}
        menuAlwaysIsOpen={menuAlwaysIsOpen}
      />
      {refValues.length > 0 && (
        <>
          {!complement && !moreInfoLabel && isOpen && (
            <HideTopOfWrapperOptions />
          )}
          <OptionPickerBoxWrapper
            hasBorderTop={!!complement || !!moreInfoLabel}
            border={border}
            boxShadowFocus={boxShadowFocus}
            isOpen={isOpen}
            role="menu"
            hasError={hasError}
            colorType={colorType}
            menuAlwaysIsOpen={menuAlwaysIsOpen}
          >
            {isOpen && withFilterText && (
              <OptionPickerFilterContainer>
                <BaSeInput
                  placeholder={searchPlaceholder}
                  iconButton={{
                    typeButton: 'base-icon',
                    name: !!inputFilter ? 'close-big' : 'search',
                    action: () => setInputFilter(''),
                  }}
                  color={colorInput}
                  value={inputFilter}
                  ref={inputFilterRef}
                  onChange={setInputFilter}
                />
              </OptionPickerFilterContainer>
            )}

            <>
              {isHeaderLabelMap(headerLabel) ? (
                <>
                  <OptionPickerOuterList isOpen={isOpen}>
                    <OptionPickerListUl
                      maxHeight={maxMenuHeight}
                      withFilterText={withFilterText}
                      role="listbox"
                    >
                      {isOpen && enableSelectAll && !maxOptionsSelected && (
                        <WrapperMultipleInformation
                          aria-selected={selectedValuesId.length > 0}
                          isOptionPickerItem={true}
                          tabIndex={0}
                          bcCHover={overrideColor.selectHoverColor ?? bcCHover}
                          onKeyDown={handleKeyDown}
                          onClick={(e) => {
                            e.preventDefault();
                            selectAll();
                          }}
                        >
                          <BaSeCheckbox
                            align="top-left"
                            isInteractive={false}
                            color={colorCheckBox}
                            label={
                              inputFilter === ''
                                ? getMessage('select.selectAll')
                                : getMessage('select.selectResults')
                            }
                            size="medium"
                            indeterminate={
                              selectedValuesId.length !== refValues.length
                            }
                            checked={selectedValuesId.length > 0}
                          />
                        </WrapperMultipleInformation>
                      )}
                      {Array.from(headerLabel.entries()).map(
                        ([groupLabel, ids]) => {
                          const groupItems = refValues
                            .filter((val) => ids.includes(val.id))
                            .filter(handleFilter);

                          return (
                            <React.Fragment key={groupLabel}>
                              {isOpen && !!groupItems.length && (
                                <OptionPickerHeaderLabelContainer>
                                  <BaSeSmall1
                                    color={
                                      BaSeTheme.colors.institucionais
                                        .cinzaSebrae60
                                    }
                                    isBold={true}
                                  >
                                    {groupLabel}
                                  </BaSeSmall1>
                                </OptionPickerHeaderLabelContainer>
                              )}

                              {groupItems.map(renderOption)}
                              {!filteredValues.length &&
                                !!optionNotFoundLabel && (
                                  <WrapperOption
                                    onClick={() => setInputFilter('')}
                                    ignoreFocus={true}
                                    multiple={true}
                                    bcCHover={bcCHover}
                                    bgC={bgC}
                                    isActive={false}
                                    isOptionPickerItem={true}
                                  >
                                    <BaSeParagraph>
                                      {optionNotFoundLabel}
                                    </BaSeParagraph>
                                  </WrapperOption>
                                )}
                            </React.Fragment>
                          );
                        },
                      )}
                    </OptionPickerListUl>
                  </OptionPickerOuterList>
                </>
              ) : (
                <>
                  {!!headerLabel && isOpen && (
                    <OptionPickerHeaderLabelContainer>
                      <BaSeSmall1
                        color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                        isBold={true}
                      >
                        {headerLabel}
                      </BaSeSmall1>
                    </OptionPickerHeaderLabelContainer>
                  )}
                  <OptionPickerOuterList isOpen={isOpen}>
                    <OptionPickerListUl
                      as={withVirtualList ? 'div' : 'ul'}
                      maxHeight={maxMenuHeight}
                      withFilterText={withFilterText}
                      role="listbox"
                    >
                      {enableSelectAll && !maxOptionsSelected && (
                        <WrapperMultipleInformation
                          aria-selected={selectedValuesId.length > 0}
                          isOptionPickerItem={true}
                          tabIndex={0}
                          bcCHover={overrideColor.selectHoverColor ?? bcCHover}
                          onKeyDown={handleKeyDown}
                          onClick={(e) => {
                            e.preventDefault();
                            selectAll();
                          }}
                        >
                          <BaSeCheckbox
                            align="top-left"
                            isInteractive={false}
                            color={colorCheckBox}
                            label={
                              inputFilter === ''
                                ? getMessage('select.selectAll')
                                : getMessage('select.selectResults')
                            }
                            size="medium"
                            indeterminate={
                              selectedValuesId.length !== refValues.length
                            }
                            checked={selectedValuesId.length > 0}
                          />
                        </WrapperMultipleInformation>
                      )}

                      {filteredValues.length && withVirtualList ? (
                        <VirtualList
                          component={WrapperVirtualList}
                          data={filteredValues}
                          height={extractPixels(maxMenuHeight) - rowHeight}
                          itemHeight={rowHeight}
                          itemKey="id"
                          styles={{
                            verticalScrollBar: {
                              width: 4,
                              borderRadius: 100,
                              cursor: 'pointer',
                              background:
                                BaSeTheme.colors.institucionais.cinzaSebrae90,
                            },
                            verticalScrollBarThumb: {
                              width: 4,
                              borderRadius: 100,
                              cursor: 'pointer',
                              background:
                                BaSeTheme.colors.institucionais.cinzaSebrae45,
                            },
                          }}
                        >
                          {renderOption}
                        </VirtualList>
                      ) : (
                        filteredValues.map(renderOption)
                      )}

                      {!filteredValues.length && !!optionNotFoundLabel && (
                        <WrapperOption
                          onClick={() => setInputFilter('')}
                          ignoreFocus={true}
                          multiple={true}
                          bcCHover={bcCHover}
                          bgC={bgC}
                          isActive={false}
                          isOptionPickerItem={false}
                        >
                          <BaSeParagraph>{optionNotFoundLabel}</BaSeParagraph>
                        </WrapperOption>
                      )}
                    </OptionPickerListUl>
                  </OptionPickerOuterList>
                </>
              )}
            </>
            {!!extraActions?.length && isOpen && (
              <>
                <OptionPickerExtraActionsHeaderWrapper>
                  <BaSeSmall1
                    isBold={true}
                    color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                  >
                    {getMessage('select.extraActions')}
                  </BaSeSmall1>
                </OptionPickerExtraActionsHeaderWrapper>
                {extraActions.map((exact, index) => (
                  <OptionPickerExtraActionButton
                    isLast={index === extraActions.length - 1}
                    onClick={() => extraActionCall(exact.action)}
                    key={exact.label}
                    onFocus={() => addIndexExtra(index)}
                    onBlur={() => removeIndexExtra(index)}
                  >
                    {!!exact.icon && (
                      <BaSeIcon
                        name={exact.icon}
                        size={20}
                        description={exact.label}
                        color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                      />
                    )}
                    <BaSeParagraph
                      color={BaSeTheme.colors.institucionais.cinzaSebrae30}
                      isBold={indexInExtra(index)}
                    >
                      {exact.label}
                    </BaSeParagraph>
                  </OptionPickerExtraActionButton>
                ))}
              </>
            )}
          </OptionPickerBoxWrapper>
        </>
      )}
      {!!complement && (
        <WrapperHelper>
          <BaSeHelperText
            isItalic={true}
            size="medium"
            color={mapBaSeSelectColor[colorType].iconColor}
            label={complement}
          />
        </WrapperHelper>
      )}
      {!!moreInfoLabel && (
        <MoreInfoContainer>
          <BaSeHelperText
            size="small"
            label={moreInfoLabel}
            details={moreInfoDetails}
            color={bgC}
          />
        </MoreInfoContainer>
      )}
    </WrapperSelect>
  );
}
